import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { fmt } from "@/lib/format";
import { formatApiError } from "@/lib/errors";

export default function AdminBankTransfers() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () =>
    api
      .get(`/admin/bank-transfers${filter === "all" ? "" : `?status=${filter}`}`)
      .then(({ data }) => setList(data))
      .catch(() => {});

  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    try {
      await api.post(`/admin/bank-transfers/${id}/approve`);
      toast.success("Approved — bricks assigned");
      setViewing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed"));
    }
  };

  const reject = async () => {
    if (!reason.trim()) {
      toast.error("Provide a reason");
      return;
    }
    try {
      await api.post(`/admin/bank-transfers/${rejecting.id}/reject`, { reason });
      toast.success("Rejected");
      setRejecting(null);
      setReason("");
      setViewing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed"));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">Payments</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-6">
        Bank transfers
      </h1>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="pending" data-testid="bt-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="bt-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="bt-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="bt-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-6 space-y-3">
        {list.length === 0 ? (
          <div className="bp-surface rounded-2xl p-12 text-center text-gray-500">
            No submissions in this view
          </div>
        ) : (
          list.map((bt) => (
            <div key={bt.id} className="bp-surface rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`bt-row-${bt.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-white">{bt.depositor_name}</div>
                <div className="text-xs text-gray-400">{bt.user_email}</div>
              </div>
              <div className="text-xs text-gray-500">
                <div className="font-mono text-[#D4AF37]">{fmt(bt.amount, { decimals: true })}</div>
                <div className="uppercase">{bt.account_used}</div>
              </div>
              <StatusPill status={bt.status} />
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewing(bt)} data-testid={`bt-review-${bt.id}`}>
                Review
              </Button>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Bank transfer review</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <Info label="Depositor" value={viewing.depositor_name} />
                <Info label="Investor" value={viewing.user_name} />
                <Info label="Investor email" value={viewing.user_email} />
                <Info label="Amount" value={fmt(viewing.amount, { decimals: true })} />
                <Info label="Account used" value={viewing.account_used?.toUpperCase()} />
                <Info label="Reference" value={viewing.reference_number || "—"} />
                <Info label="Purpose" value={viewing.purpose?.replace("_", " ")} />
                <Info label="Bricks" value={viewing.brick_ids?.length || 0} />
                <div className="md:col-span-2">
                  <Info label="Note" value={viewing.note || "—"} />
                </div>
                <Info label="Submitted" value={new Date(viewing.submitted_at).toLocaleString()} />
                <Info label="Status" value={viewing.status} />
              </div>

              <div>
                <div className="overline text-[10px] mb-2 flex items-center gap-1">
                  Uploaded teller
                  <a href={viewing.teller_url} target="_blank" rel="noreferrer" className="text-[#D4AF37]">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <a href={viewing.teller_url} target="_blank" rel="noreferrer">
                  <img src={viewing.teller_url} alt="teller" className="max-h-96 rounded-lg border border-white/10" />
                </a>
              </div>

              {viewing.rejection_reason && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300">
                  Rejection: {viewing.rejection_reason}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {viewing?.status === "pending" && (
              <>
                <Button variant="outline" className="rounded-full text-red-400 border-red-500/40 hover:bg-red-500/10" onClick={() => setRejecting(viewing)} data-testid="bt-reject-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button className="btn-gold rounded-full" onClick={() => approve(viewing.id)} data-testid="bt-approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approve & fulfill
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && (setRejecting(null), setReason(""))}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Rejection reason</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason visible to the investor" className="bg-[#050914] border-white/10" data-testid="bt-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setReason(""); }}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full" onClick={reject} data-testid="bt-confirm-reject">
              Confirm rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline text-[10px]">{label}</div>
      <div className="text-white mt-1">{value || "—"}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "#F59E0B" },
    approved: { c: "#10B981" },
    rejected: { c: "#EF4444" },
  };
  const s = map[status] || map.pending;
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full" style={{ color: s.c, background: `${s.c}15`, border: `1px solid ${s.c}40` }}>
      {status}
    </span>
  );
}
